import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import SweetAlert from 'react-bootstrap-sweetalert';
import AdminPage from './admin/AdminPage';
import useAuth from '../hooks/useAuth';
import useCallApiAndLoad from '../hooks/useCallApiAndLoad';
import { getRolesEndpoint } from '../services/rolesService';
import useNavBar from '../hooks/useNavBar';
import useUserCountry from '../hooks/useUserCountry';

export interface Role {
  id: string | number;
  name: string;
}

interface NavBarPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignedRoles: Role[];
}

export interface RolesService {
  getUserRoles: (userId: string, token: string) => any;
  assignRole: (userId: string, roleId: string, token: string) => any;
  removeRole: (userId: string, roleId: string, token: string) => any;
}

interface Props {
  userId: string;
  userEmail?: string;
  service: RolesService;
  pageName: string;
  pageParentName: string;
}

const RolesManagement = ({
  userId,
  userEmail,
  service,
  pageName,
  pageParentName,
}: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getNavBar } = useNavBar();

  const [processingId, setProcessingId] = useState<string | number | null>(
    null,
  );
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [assignedRoles, setAssignedRoles] = useState<Role[]>([]);

  const [navBarPreviewOpen, setNavBarPreviewOpen] = useState(false);

  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<any>({
    success: true,
    title: '',
    text: '',
    onConfirm: () => setSweetAlert(false),
  });

  const loadData = useCallback(async () => {
    try {
      const globalRes = await callEndpoint(getRolesEndpoint(token));
      const userRes = await callEndpoint(service.getUserRoles(userId, token));

      const globalListRaw = Array.isArray(globalRes.data)
        ? globalRes.data
        : globalRes.data?.data || [];
      const userListRaw = Array.isArray(userRes.data)
        ? userRes.data
        : userRes.data?.data || [];

      const normalizeRoles = (list: any[]): Role[] =>
        list
          .map((role: any) => {
            if (typeof role === 'string') {
              return { id: role, name: role };
            }
            return {
              id:
                role.id ??
                role.role_id ??
                role.code ??
                role.tid ??
                role.key ??
                role.value,
              name: role.name ?? role.role_namex ?? role.label ?? '',
            };
          })
          .filter((role) => role.id && role.name);

      setAllRoles(normalizeRoles(globalListRaw));
      setAssignedRoles(normalizeRoles(userListRaw));
    } catch (error) {
      setAlertProps({
        danger: true,
        title: 'Error',
        text: 'Ocurrió un error al cargar los roles.',
        onConfirm: () => setSweetAlert(false),
      });
      setSweetAlert(true);
    }
  }, [token, userId, service, callEndpoint]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availableRoles = allRoles.filter(
    (role) =>
      !assignedRoles.some(
        (assigned) =>
          `${assigned.id}` === `${role.id}` ||
          `${assigned.name}` === `${role.name}`,
      ),
  );

  const handleAssign = async (role: Role) => {
    setProcessingId(role.id);
    const { status } = await callEndpoint(
      service.assignRole(userId, role.id.toString(), token),
    );

    if (status === 200 || status === 201) {
      setAssignedRoles((prev) => [...prev, role]);
    } else {
      setAlertProps({
        danger: true,
        title: 'Error',
        text: 'No se pudo asignar el rol',
        onConfirm: () => setSweetAlert(false),
      });
      setSweetAlert(true);
    }
    setProcessingId(null);
  };

  const handleRemove = async (role: Role) => {
    setProcessingId(role.id);
    const matching = allRoles.find(
      (r) =>
        `${r.id}` === `${role.id}` ||
        `${r.name}` === `${role.id}` ||
        `${r.name}` === `${role.name}`,
    );
    const roleIdToSend = matching?.id ?? role.id;

    const { status } = await callEndpoint(
      service.removeRole(userId, roleIdToSend.toString(), token),
    );

    if (status === 200 || status === 204) {
      setAssignedRoles((prev) => prev.filter((r) => r.id !== role.id));
    } else {
      setAlertProps({
        danger: true,
        title: 'Error',
        text: 'No se pudo eliminar el rol',
        onConfirm: () => setSweetAlert(false),
      });
      setSweetAlert(true);
    }
    setProcessingId(null);
  };

  const headerComponent = (
    <div className="mb-4 d-flex align-items-center justify-content-between">
      <div>
        {userEmail ? (
          <h3>{userEmail}</h3>
        ) : (
          isLoading && (
            <small className="text-muted">Cargando información...</small>
          )
        )}
      </div>
      <Button color="info" size="sm" onClick={() => setNavBarPreviewOpen(true)}>
        <i className="ni ni-map-big mr-2" />
        Ver NavBar
      </Button>
    </div>
  );

  return (
    <AdminPage
      name={pageName}
      parentName={pageParentName}
      header={headerComponent}
    >
      {sweetAlert && <SweetAlert {...alertProps}>{alertProps.text}</SweetAlert>}

      {isLoading && allRoles.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <Row>
          {/* --- COLUMNA IZQUIERDA --- */}
          <Col md="6">
            <div style={{ height: '500px', overflow: 'auto' }}>
              <Table className="align-items-center" responsive hover>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Disponibles</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {availableRoles.length === 0 && (
                    <tr>
                      <td colSpan={2} className="text-center text-muted">
                        No hay roles disponibles
                      </td>
                    </tr>
                  )}
                  {availableRoles.map((role) => (
                    <tr key={role.id}>
                      <td>{role.name}</td>
                      <td className="text-right">
                        <Button
                          size="sm"
                          color="success"
                          className="btn-icon-only rounded-circle shadow-none"
                          onClick={() => handleAssign(role)}
                          disabled={!!processingId}
                          title="Asignar"
                        >
                          {processingId === role.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="fas fa-plus" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>

          {/* --- COLUMNA DERECHA --- */}
          <Col md="6">
            <div style={{ height: '500px', overflow: 'auto' }}>
              <Table className="align-items-center" responsive hover>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Asignados</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {assignedRoles.length === 0 && (
                    <tr>
                      <td colSpan={2} className="text-center text-muted">
                        Sin roles asignados
                      </td>
                    </tr>
                  )}
                  {assignedRoles.map((role) => (
                    <tr key={role.id}>
                      <td>{role.name}</td>
                      <td className="text-right">
                        <Button
                          size="sm"
                          color="danger"
                          className="btn-icon-only rounded-circle shadow-none"
                          onClick={() => handleRemove(role)}
                          disabled={!!processingId}
                          title="Remover"
                        >
                          {processingId === role.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="fas fa-times" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      )}

      <NavBarPreviewModal
        isOpen={navBarPreviewOpen}
        onClose={() => setNavBarPreviewOpen(false)}
        assignedRoles={assignedRoles}
      />
    </AdminPage>
  );
};

export default RolesManagement;

// ---------------------------------------------------------------------------

const NavBarPreviewModal = ({
  isOpen,
  onClose,
  assignedRoles,
}: NavBarPreviewModalProps) => {
  const { getNavBar } = useNavBar();
  const { getCurrentCountry } = useUserCountry();
  const country = getCurrentCountry()?.code ?? 'SV';

  const navBarPreview = getNavBar(
    country,
    assignedRoles.map((r) => r.name),
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        Vista previa de NavBar — {country}
      </ModalHeader>
      <ModalBody>
        {navBarPreview.length === 0 ? (
          <p className="text-muted text-center py-3">
            Ningún elemento de menú visible con los roles asignados para este
            país.
          </p>
        ) : (
          <Table bordered size="sm">
            <thead className="thead-light">
              <tr>
                <th>Menú</th>
                <th>Sub-ítems visibles</th>
              </tr>
            </thead>
            <tbody>
              {navBarPreview.map((item) => (
                <tr key={item.state}>
                  <td>
                    <i className={`${item.icon} mr-2`} />
                    <strong>{item.name}</strong>
                  </td>
                  <td>
                    {item.views.length === 0 ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <ul className="mb-0 pl-3">
                        {item.views.map((view) => (
                          <li key={view.path}>
                            {view.name}{' '}
                            <code className="text-muted">{view.path}</code>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </ModalBody>
    </Modal>
  );
};
